import { useEffect, useState } from "react";
import { CloudUpload, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Progress } from "./ui/progress";
import api from "@/lib/api";
import { toast } from "sonner";

// One-click migration of remaining Emergent-Object-Storage files to the Bunny
// CDN, callable from the admin dashboard. Migrates in batches of 25 files at a
// time so the UI can render live progress and the backend never has to hold
// hundreds of files in memory in a single request.
const BATCH_SIZE = 25;

export default function MigrateToBunnyDialog() {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [migratedNow, setMigratedNow] = useState(0);
  const [errors, setErrors] = useState([]);
  const [done, setDone] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/migration-stats");
      setStats(data);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => {
    if (open) {
      setMigrating(false);
      setProgress(0);
      setMigratedNow(0);
      setErrors([]);
      setDone(false);
      loadStats();
    }
  }, [open]);

  const runMigration = async () => {
    if (!stats || stats.legacy_count === 0) return;
    setMigrating(true);
    setDone(false);
    setErrors([]);
    const total = stats.legacy_count;
    let processed = 0;
    let localErrors = [];
    while (true) {
      try {
        const { data } = await api.post(
          `/admin/migrate-to-bunny?limit=${BATCH_SIZE}`
        );
        const batchMigrated = data.migrated_count || 0;
        const batchErrors = data.errors || [];
        processed += batchMigrated;
        localErrors = [...localErrors, ...batchErrors];
        setMigratedNow(processed);
        setErrors(localErrors);
        setProgress(Math.min(100, Math.round((processed / Math.max(total, 1)) * 100)));
        if (data.remaining_legacy_files === 0 || (batchMigrated === 0 && batchErrors.length === 0)) {
          break;
        }
      } catch (e) {
        localErrors.push({ error: e?.response?.data?.detail || e.message });
        setErrors(localErrors);
        break;
      }
    }
    setProgress(100);
    setDone(true);
    setMigrating(false);
    await loadStats();
    if (localErrors.length === 0) {
      toast.success(`${processed} ficheiro(s) migrado(s) para a CDN Bunny`);
    } else {
      toast.warning(`Migração concluída com ${localErrors.length} erro(s)`);
    }
  };

  const nothingToMigrate = stats && stats.legacy_count === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-10 sm:h-11 px-4 sm:px-5 text-[10px] sm:text-xs tracking-[0.2em] uppercase flex-1 md:flex-none"
          data-testid="open-migrate-bunny-dialog"
        >
          <CloudUpload className="w-4 h-4 mr-2" /> CDN
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-[#F9F6F0] border-[#EAE3DB]" data-testid="migrate-bunny-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1A1412] tracking-wide">
            Migrar ficheiros para a CDN
          </DialogTitle>
          <DialogDescription className="sr-only">
            Copia os ficheiros ainda alojados no armazenamento antigo para a CDN Bunny.
          </DialogDescription>
        </DialogHeader>

        {!stats ? (
          <div className="text-[#5C4A43] text-sm py-6 text-center uppercase tracking-widest">
            A carregar estado…
          </div>
        ) : (
          <div className="space-y-5">
            <div className="text-[13px] text-[#3B2B23] leading-relaxed">
              A CDN é a <strong>Bunny.net</strong> (<span className="font-mono text-[11px]">{stats.cdn_hostname}</span>) — entrega os ficheiros globalmente em milissegundos aos clientes.
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-[#EAE3DB] bg-white p-3 text-center">
                <div className="text-[9px] tracking-[0.2em] uppercase text-[#8B5E3C]">Total</div>
                <div className="text-2xl font-bold text-[#1A1412] mt-1" data-testid="cdn-stat-total">{stats.total_count}</div>
              </div>
              <div className="rounded-xl border border-[#EAE3DB] bg-white p-3 text-center">
                <div className="text-[9px] tracking-[0.2em] uppercase text-[#8B5E3C]">Já na CDN</div>
                <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="cdn-stat-migrated">{stats.migrated_count}</div>
              </div>
              <div className="rounded-xl border border-[#EAE3DB] bg-white p-3 text-center">
                <div className="text-[9px] tracking-[0.2em] uppercase text-[#8B5E3C]">Por migrar</div>
                <div className="text-2xl font-bold text-[#C07C5B] mt-1" data-testid="cdn-stat-legacy">{stats.legacy_count}</div>
              </div>
            </div>

            {migrating || done ? (
              <div className="space-y-2">
                <Progress value={progress} className="h-2 bg-[#EAE3DB]" />
                <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-[#5C4A43]">
                  <span data-testid="migrate-progress-label">
                    {migrating ? "A migrar…" : "Concluído"}
                  </span>
                  <span>{migratedNow} / {stats.legacy_count + stats.migrated_count}</span>
                </div>
                {errors.length > 0 && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-[11px] text-red-800" data-testid="migrate-errors">
                    <div className="flex items-center gap-2 font-semibold mb-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> {errors.length} erro(s)
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5">
                      {errors.slice(0, 5).map((e, i) => (
                        <li key={i}>{e.error || e.path}</li>
                      ))}
                      {errors.length > 5 && <li>+ {errors.length - 5} …</li>}
                    </ul>
                  </div>
                )}
                {done && errors.length === 0 && (
                  <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="migrate-success">
                    <CheckCircle2 className="w-4 h-4" /> Todos os ficheiros estão agora na CDN.
                  </div>
                )}
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="flex-1 rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-11 text-xs tracking-[0.2em] uppercase"
                  data-testid="migrate-cancel"
                >
                  Fechar
                </Button>
                <Button
                  onClick={runMigration}
                  disabled={nothingToMigrate || !stats.bunny_configured}
                  className="flex-1 rounded-full bg-[#3B2B23] text-[#F9F6F0] hover:bg-[#1A1412] h-11 text-xs tracking-[0.2em] uppercase"
                  data-testid="migrate-run"
                >
                  <CloudUpload className="w-4 h-4 mr-2" />
                  {nothingToMigrate ? "Nada a migrar" : `Migrar ${stats.legacy_count}`}
                </Button>
              </div>
            )}

            {!stats.bunny_configured && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900">
                Bunny Storage não está configurado. Defina as variáveis <code>BUNNY_STORAGE_*</code> no <code>.env</code> do backend.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
